import { useState, useCallback } from 'react'
import styled from 'styled-components'
import {
    useActions,
    useTranslation,
    useTypedSelector,
} from '../../../../../../hooks'
import BackButton from '../../../../../reusable/buttons/BackButton'
import CompanyInformationFrame from '../../../../../reusable/frames/CompanyInformationFrame'
import LineOfContactsFrame from '../../../../../reusable/frames/LineOfContactsFrame'
import { HeadingSecondary } from '../../../../../styled/typography'
import PartnerReportQuestionsFrame from './frames/PartnerReportQuestionsFrame'
import PartnerOrganizationStatisticsFrame from './frames/PartnerOrganizationStatisticsFrame'

const Container = styled.div`
    width: 100%;
    padding: 4rem;
    position: relative;
`

const Navigation = styled.div`
    border-bottom: 1px solid ${({ theme }) => `${theme.primary}20`};
    width: 100%;
    display: flex;
    align-items: center;
    padding-top: 2rem;
    margin-bottom: 2rem;
`

interface IsActive {
    isActive: boolean
}

const NavigationLink = styled.div<IsActive>`
    padding-bottom: 1rem;
    opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
    color: ${({ theme }) => theme.primary};
    font-size: 1.8rem;
    font-weight: 600;
    margin-right: 3.5rem;
    border-bottom: ${({ theme, isActive }) =>
        isActive ? `2px solid ${theme.blue} ` : 'none'};
    cursor: pointer;
`

interface SingleCompanyProps {
    setCurrentFrameNumber: (frameNumber: number) => void
}

type TabName = 'information' | 'caseworkers' | 'report-questions' | 'statistics'

const SingleCompany: React.FC<SingleCompanyProps> = ({
    setCurrentFrameNumber,
}) => {
    const { selectedOrganizationInformation } = useTypedSelector(
        (state) => state.caseworker.clientOrgs
    )

    const { organization: partnerOrganization } = useTypedSelector(
        (state) => state.caseworker.identity
    )

    const translation = useTranslation()
    const { removeCaseworkerFromOrganization } = useActions()

    const handleDeleteContact = useCallback(
        (contactId: number) => {
            if (partnerOrganization && selectedOrganizationInformation) {
                return removeCaseworkerFromOrganization(
                    partnerOrganization.id,
                    selectedOrganizationInformation.id,
                    contactId
                )
            }
        },
        [
            partnerOrganization,
            selectedOrganizationInformation,
            removeCaseworkerFromOrganization,
        ]
    )

    const [currentTab, setCurrentTab] = useState<TabName>('information')

    const tabs: { name: TabName; label: string }[] = [
        {
            name: 'information',
            label: translation.singleClientViewPartner.information,
        },
        {
            name: 'caseworkers',
            label: translation.singleClientViewPartner.lineOfContact,
        },
        {
            name: 'report-questions',
            label: translation.clientView.reportQuestions,
        },
        {
            name: 'statistics',
            label: translation.statisticsPage.statistics,
        },
    ]

    const renderFrame = () => {
        switch (currentTab) {
            case 'information':
                return (
                    <CompanyInformationFrame
                        client={
                            selectedOrganizationInformation!.contact_person
                                ?.data
                        }
                        clientOrganization={selectedOrganizationInformation!}
                        partnerId={partnerOrganization?.id}
                    />
                )
            case 'caseworkers':
                return (
                    <LineOfContactsFrame
                        inviteModalType="assign"
                        inviteButtonLabel={
                            translation.singleClientViewPartner.assignCaseworker
                        }
                        onDeleteContact={handleDeleteContact}
                        disableDragAndDrop={true}
                    />
                )
            case 'report-questions':
                return (
                    <PartnerReportQuestionsFrame
                        partnerId={partnerOrganization!.id}
                        organizationId={selectedOrganizationInformation!.id}
                    />
                )
            case 'statistics':
                return (
                    <PartnerOrganizationStatisticsFrame
                        partnerId={partnerOrganization!.id}
                        organizationId={selectedOrganizationInformation!.id}
                    />
                )
        }
    }

    return (
        <Container>
            <BackButton onClick={() => setCurrentFrameNumber(1)} />
            <HeadingSecondary style={{ marginTop: '1.4rem' }}>
                {selectedOrganizationInformation?.name}
            </HeadingSecondary>
            <Navigation>
                {tabs.map((tab) => (
                    <NavigationLink
                        key={tab.name}
                        isActive={currentTab === tab.name}
                        onClick={() => setCurrentTab(tab.name)}
                    >
                        {tab.label}
                    </NavigationLink>
                ))}
            </Navigation>
            {renderFrame()}
        </Container>
    )
}

export default SingleCompany
